import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuthStore } from '../store';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  // timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

// 🔐 REQUEST INTERCEPTOR
api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().token;

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

// 🚨 RESPONSE INTERCEPTOR
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const msg =
      error.response?.data?.error ||
      error.message ||
      'Something went wrong';

    if (status === 401) {
      console.warn('Unauthorized - redirecting');

      // ❗ don't instantly wipe token unless needed
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }

    if (status >= 500) {
      toast.error(`Server error: ${msg}`);
    }

    return Promise.reject(error);
  }
);

export default api;

// ── Domain helpers ────────────────────────────────────────────────────────────
export const authApi = {
  login:  (body) => api.post('/auth/login', body),

  // OTP login flow: /auth/login sends the OTP to the user's email/phone,
  // then /api/auth/otp/verify checks the code and completes the sign-in.
  sendOtp:   (body) => api.post('/auth/login', body),
  verifyOtp: (body) => api.post('/auth/otp/verify', body),

  me:     ()     => api.get('/auth/me'),
  logout: ()     => api.post('/auth/logout'),
};

export const domoApi = {
  connect:    (creds)          => api.post('/domo/connect', creds),
  datasets:   (creds)          => api.post('/domo/datasets', creds),
  schema:     (id, creds)      => api.post(`/domo/datasets/${id}/schema`, creds),
  pages:      (creds)          => api.post('/domo/pages', creds),
  cards:      (pageId, creds)  => api.post(`/domo/pages/${pageId}/cards`, creds),
  beastModes: (dsId, creds)    => api.post(`/domo/datasets/${dsId}/beastmodes`, creds),
  convertDax: (body)           => api.post('/domo/convert-dax', body),
};

export const pbiApi = {
  connect:    (creds) => api.post('/powerbi/connect', creds),
  workspaces: (creds) => api.post('/powerbi/workspaces', creds),
  datasources: (creds) => api.post('/powerbi/datasources', creds),
};

export const datasourcesApi = {
  list: (datasetIds) => api.post('/powerbi/datasources', { datasetIds }),
};

export const migrationsApi = {
  list:   ()        => api.get('/migrations'),
  get:    (id)      => api.get(`/migrations/${id}`),
  create: (body)    => api.post('/migrations', body),
  retry:  (id)      => api.post(`/migrations/${id}/retry`),
  cancel: (id)      => api.post(`/migrations/${id}/cancel`),
};

export const mappingsApi = {
  list:   ()         => api.get('/mappings'),
  create: (body)     => api.post('/mappings', body),
  update: (id, body) => api.put(`/mappings/${id}`, body),
  remove: (id)       => api.delete(`/mappings/${id}`),
};

export const dashboardApi = {
  stats: () => api.get('/dashboard/stats'),
  downloadResult: (jobId) => api.get(`/dashboard/migrations/${jobId}/result`, { responseType: 'blob' }),
};

export const discoveryApi = {
  discovery: () => api.get('/domo/discovery'),
  analyze: (payload) => api.post('/domo/discovery/analyze', payload)
};

export const assessmentApi = {
  // Legacy (synchronous) — kept for backward compatibility
  assess: () => api.get('/domo/assess'),

  // Job-based (async) assessment endpoints
  start:     (body)                    => api.post('/assessment/start', body),
  // Used instead of /assessment/start when every resource being assessed
  // came from an offline JSON upload (source === 'file') rather than a
  // live Domo connection — see ProjectDetailPage's handleStartAssessment.
  startOffline: (body)                 => api.post('/assessment/offline-start', body),
  // `params` scopes status to one specific entity so independent pages
  // polling this endpoint don't all see each other's progress:
  //   - { assessId }  — used by /assess/:id (a specific historical run)
  //   - { projectId } — used by /projects/:id (that project's own run)
  // Omit params entirely to get the legacy "whatever is globally current"
  // behavior (used by the plain /assess live view).
  status:    (params)                  => api.get('/assessment/status', { params: params && Object.keys(params).length ? params : undefined }),
  result:    ()                        => api.get('/assessment/result'),
  pairwise:  ({ page, limit, filter }) => api.get('/assessment/pairwise', { params: { page, limit, filter } }),
  cancel:    ()                        => api.post('/assessment/cancel'),
  logs:      (jobId)                   => api.get(`/assessment/logs/${jobId}`),

  // Apps & Pages picker (used by the "Start Assessment" scope selection modal)
  appsPages: () => api.get('/domo/apps-pages'),

  // Assessment run history (used by the "History" popup on the Assess page,
  // and by the Project Detail page's own history card). `params` accepts
  // an optional { projectId } filter — if the backend doesn't support it
  // yet, the caller falls back to filtering the full list client-side.
  history:       (params)  => api.get('/assessment/history', { params }),
  historyDetail: (id)      => api.get(`/assessment/history/${id}`),

  // Pairwise similarity dimension weights (used by the Merge Sets / Pairwise
  // Detail algorithm). Weights must sum to <= 1; the backend is the source
  // of truth so the UI stays in sync with whatever the scoring engine uses.
  getPairwiseWeights: () => api.get('/assessment/weights'),
  updatePairwiseWeights: (body) => api.put('/assessment/weights', body),

  // Page Complexity factor weights + Low/Medium/High score thresholds
  // (used by the Pages tab / Overall Complexity rollup). Editable from the
  // Assessment Information -> Technical Details modal, same pattern as the
  // pairwise weights above.
  getPageComplexityConfig: () => api.get('/assessment/page-complexity-config'),
  updatePageComplexityConfig: (body) => api.put('/assessment/page-complexity-config', body),
};

export const projectsApi = {
  list:   ()          => api.get('/projects'),
  get:    (id)         => api.get(`/projects/${id}`),
  create: (body)       => api.post('/projects', body),
  update: (id, body)   => api.put(`/projects/${id}`, body),
  remove: (id)          => api.delete(`/projects/${id}`),

  // Merge two or more projects into one. Payload is just the new project's
  // name plus the ids of the projects being merged — the backend is
  // responsible for consolidating resources/users/comments/history.
  merge: (body) => api.post('/projects/merge', body),

  // Apps / Reports (Domo pages) assigned to a project for assessment scope.
  // When an offline file is attached (addResourcesWithFile), it's sent as
  // multipart/form-data: resources/apps/pages as JSON-stringified fields
  // alongside the raw file under `file` — the backend reads the file
  // itself and matches/merges it with whatever apps/pages were chosen,
  // instead of the frontend parsing the file's contents.
  addResources:    (id, body) => api.post(`/projects/${id}/resources`, body),
  // `fileTypes` is a JSON-stringified array of per-file category tags
  // (app/page/card/dataset/beastmode/datasource), same order/length as
  // `files` — lets the backend know how to interpret each uploaded file
  // without the frontend parsing its contents.
  addResourcesWithFile: (id, { resources, apps, pages, files, fileTypes }) => {
    const formData = new FormData();
    formData.append('resources', JSON.stringify(resources || []));
    formData.append('apps', JSON.stringify(apps || []));
    formData.append('pages', JSON.stringify(pages || []));
    formData.append('fileTypes', JSON.stringify(fileTypes || []));
    (files || []).forEach(file => formData.append('files', file, file.name));
    return api.post(`/projects/${id}/resources`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  removeResource:  (id, resourceId) => api.delete(`/projects/${id}/resources/${resourceId}`),

  // User assignment
  assignUsers:   (id, body)      => api.post(`/projects/${id}/users`, body),
  unassignUser:  (id, userId)    => api.delete(`/projects/${id}/users/${userId}`),

  // Comments
  addComment:    (id, body) => api.post(`/projects/${id}/comments`, body),

  // Lock / unlock — once locked, no edits to the project, its resources,
  // or its assigned users are allowed until it is unlocked again.
  lock:   (id) => api.post(`/projects/${id}/lock`),
  unlock: (id) => api.post(`/projects/${id}/unlock`),
};

export const usersApi = {
  list:   ()         => api.get('/users'),
  get:    (id)        => api.get(`/users/${id}`),
  // Creating/updating/removing users is admin-only. The backend must
  // enforce this independently of the frontend's role check (see
  // UsersPage.js) — a non-admin calling these directly should get a 403.
  create: (body)      => api.post('/users', body),
  update: (id, body)  => api.put(`/users/${id}`, body),
  remove: (id)         => api.delete(`/users/${id}`),
};

export const userDetailsApi = {
  get:    ()     => api.get('/user/details'),
  save:   (body) => api.post('/user/details', body),
  update: (body) => api.put('/user/details', body),
  delete: ()     => api.delete('/user/details'),
};

export const settingsApi = {
  saveDomo: (body) => api.post('/settings/domo', body),
  savePbi: (body) => api.post('/settings/pbi', body),
  getDetails: () => api.get('/settings/details'),
};